import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { InvoiceStanding } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceStanding";
import { InvoiceType } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceType";
import { FilePdfIcon } from "@phosphor-icons/react/FilePdf";
import { asLocalDate } from "@freightsimple/time/branded-types";
import type { Column } from "@freightsimple/ui-core/Table/Table";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { CardBrandIcon } from "@freightsimple/ui-core/Display/CardBrandIcon";
import { DateCell } from "@freightsimple/ui-core/Cells/DateCell";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Table } from "@freightsimple/ui-core/Table/Table";
import { T } from "@freightsimple/ui-core/Typography/T";
import { paymentsCardBrandFor } from "@/Shared/Payments/Helpers/paymentsCardBrandFor";
import { formatPostalCode } from "@/Helpers/formatPostalCode";
import { useDownloadsInFlight } from "@/Hooks/useDownloadsInFlight";
import ShipmentSmallFromTo from "@/Shared/Shipment/CustomComponents/ShipmentSmallFromTo";
import { invoiceTypeDescription } from "@/Shared/Invoices/Helpers/invoiceTypeDescription";
import { formatIdentifierDateLine } from "@/Shared/Invoices/Helpers/invoiceUtils";
import { InvoicesStatusPill } from "@/Shared/Invoices/CustomComponents/InvoicesStatusPill";
import { ProhibitIcon } from "@phosphor-icons/react/Prohibit";

export interface InvoicesTableRow {
  invoice: Invoice;
  shipment: Shipment;
  onViewShipment: () => void;
  onDownloadInvoice: () => Promise<void>;
}

interface InvoicesTableProps {
  rows: InvoicesTableRow[];
}

function amountPrefix(invoice: Invoice): string {
  if (
    invoice.invoiceType === InvoiceType.ShipmentRefund ||
    invoice.invoiceType === InvoiceType.AdditionalChargeRefund
  ) {
    return "-";
  }
  return "";
}

const columns: Column<InvoicesTableRow>[] = [
  {
    key: "charge",
    // Takes the surplus, so it lands between Charge and Status rather than in
    // the trailing actions cell, where it left the buttons marooned away from
    // the Payment icon. `width: 100%` in an auto-layout table means "have
    // whatever the others don't want", which is exactly the job - the rest of
    // the columns hug or are capped, so there is nowhere else for it to go.
    width: "100%",
    header: "Charge",
    render: ({ invoice }) => (
      <Stack gap={0} align="start">
        <T size="base" weight="medium" color="secondary">
          {invoiceTypeDescription(invoice.invoiceType)}
        </T>
        <T size="xs" weight="light" color="muted">
          {formatIdentifierDateLine(invoice)}
        </T>
      </Stack>
    ),
  },
  {
    key: "status",
    hug: true,
    header: "Status",
    // The pill says "Disputed" on its own now. It used to be a second red
    // marker stacked above a pill that said "Overdue" — one row contradicting
    // itself, which is the bug this column existed at the centre of.
    render: ({ invoice }) => (
      <Stack gap={1} align="start">
        <InvoicesStatusPill invoice={invoice} />
        {invoice.blockingBooking && (
          <Badge
            label="Blocking booking"
            variant="danger"
            size="small"
            icon={ProhibitIcon}
            tooltip="Booking is paused until this invoice is paid."
          />
        )}
      </Stack>
    ),
  },
  {
    key: "route",
    // Capped rather than hugging or flexing. Hugging forces the two business
    // names and cities onto one line (hug implies nowrap) and pushed the table
    // past the window; leaving it flexible made it swallow all the surplus
    // width, which showed up as a gap before Due. A fixed width does neither:
    // long names wrap inside it, and the leftover goes to the trailing actions
    // cell instead of between two columns.
    width: "320px",
    header: "Route",
    render: ({ shipment }) => {
      const { pickupLocation, deliveryLocation } = shipment;
      // If we don't have a business name, use the postal code
      const pickupBusinessName =
        pickupLocation.businessName ??
        (pickupLocation.address.postalCode
          ? `Picking up from : ${formatPostalCode(
              pickupLocation.address.postalCode,
            )}`
          : "");
      const deliveryBusinessName =
        deliveryLocation.businessName ??
        (deliveryLocation.address.postalCode
          ? `Delivering to : ${formatPostalCode(
              deliveryLocation.address.postalCode,
            )}`
          : "");
      return (
        <ShipmentSmallFromTo
          pickupBusinessName={pickupBusinessName}
          pickupCity={`${pickupLocation.address.city}, ${pickupLocation.address.stateOrProvinceCode}`}
          pickupReference={shipment.pickupReferenceNumber}
          deliveryBusinessName={deliveryBusinessName}
          deliveryCity={`${deliveryLocation.address.city}, ${deliveryLocation.address.stateOrProvinceCode}`}
          deliveryReference={shipment.deliveryReferenceNumber}
        />
      );
    },
  },
  {
    key: "due",
    header: "Due",
    hug: true,
    render: ({ invoice }) => (
      <DateCell
        date={asLocalDate(invoice.dueDate)}
        // Red means "this needs paying". A paid invoice's past due date is
        // history, and a disputed one is on hold with us — neither is a
        // warning, so neither goes red.
        color={invoice.standing === InvoiceStanding.Overdue ? "red" : "default"}
      />
    ),
    sortable: (a, b) => a.invoice.dueDate.localeCompare(b.invoice.dueDate),
  },
  {
    key: "amount",
    header: "Amount",
    hug: true,
    render: ({ invoice }) => {
      // The status pill sits several columns away, so the amount carries the
      // two states that change what it means: a voided amount is no longer
      // owed, and a disputed one is on hold. Red matches the disputed pill.
      const voided = invoice.standing === InvoiceStanding.Voided;
      const disputed = invoice.standing === InvoiceStanding.Disputed;
      return (
        <Row gap={1} justify="start">
          {/* Crisp `default`: the amount is the headline value of the row, which
              the rubric puts at the top tier. The currency beside it is a unit
              suffix, so it stays `muted` and the number leads. */}
          <T
            size="lg"
            weight="medium"
            nowrap
            strike={voided}
            color={voided ? "muted" : disputed ? "red" : "default"}
          >
            ${amountPrefix(invoice)}
            {invoice.amount.toFixed(2)}
          </T>
          <T size="lg" weight="medium" color="muted" nowrap strike={voided}>
            {invoice.currency === Currency.Cad ? "CAD" : "USD"}
          </T>
        </Row>
      );
    },
  },
  {
    key: "payment",
    header: "Payment",
    hug: true,
    align: "center",
    // The Row centres the card logo too: `align` only sets text-align, and the
    // logo is a block-level image that ignores it.
    render: ({ invoice }) => (
      <Row justify="center">
        {invoice.brand ? (
          <CardBrandIcon
            size="lg"
            brand={paymentsCardBrandFor(invoice.brand, invoice.paymentMethod)}
          />
        ) : (
          // A blank cell let the action buttons read as this column's content.
          <T color="faint">—</T>
        )}
      </Row>
    ),
  },
];

// One month's invoices as a ui-core data table. Replaces the hand-built
// fixed-px-column InvoiceRow cards.
export function InvoicesTable(props: InvoicesTableProps) {
  // The PDF takes a few seconds to arrive, so the clicked row's Download spins
  // until it does rather than looking like nothing happened.
  const downloads = useDownloadsInFlight();

  return (
    <Table
      columns={columns}
      data={props.rows}
      // No column below can absorb surplus width by accident: Charge claims it
      // deliberately with width 100%, Route is capped, and the rest hug. So the
      // gap between any two columns is exactly this padding, which is why it
      // has to be set wider than the default here.
      columnSpacing="wide"
      rowKey={(row) => row.invoice.invoiceIdentifier}
      // Tint the invoices actually holding up booking, so "which of these is
      // the problem?" is answerable at a glance rather than row by row.
      rowHighlight={(row) => (row.invoice.blockingBooking ? "red" : undefined)}
      rowActions={(row) => (
        <>
          <Button onClick={row.onViewShipment}>View Shipment</Button>
          {/* `display="full"` and a solid icon weight, because neither default
              worked here: inside a Table, Button collapses to icon-only, and
              Icon's default duotone draws most of the glyph at 0.2 opacity. A
              16px mostly-transparent PDF mark with no label was almost
              invisible on a dark row, and unrecognisable as "download". */}
          <Button
            icon={FilePdfIcon}
            iconWeight="bold"
            display="full"
            loading={downloads.isDownloading(row.invoice.invoiceIdentifier)}
            onClick={() =>
              downloads.download(
                row.invoice.invoiceIdentifier,
                row.onDownloadInvoice,
              )
            }
          >
            Download
          </Button>
        </>
      )}
    />
  );
}
